/**
 * Ortak Dosyalar — tenant-wide shared file drive with folders + optional
 * password-protection per file. Super admins can create/rename/delete
 * folders and upload files with an optional password + recipient list;
 * everyone else can browse, preview, and (if allowed) reveal passwords.
 *
 * Layout: 2-column split — folder tree on the left, file grid on the right.
 * The folder tree renders "Kök" as a virtual root so files uploaded without
 * a folder_id remain reachable.
 *
 * Sub-components live in sibling files to keep this module small enough for
 * babel-traverse (the combined version blew past the recursion limit).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Upload,
  Lock,
  Trash2,
  ChevronRight,
  Download,
  Loader2,
  Home,
  FolderPlus,
  Move,
  X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { sharedFilesApi } from "../../lib/copApi";
import { api as http, formatApiErrorDetail } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import { iconFor, formatSize } from "./sharedFilesHelpers";
import SharedFilePreviewModal from "./SharedFilePreviewModal";
import SharedFileUploadDialog from "./SharedFileUploadDialog";
import {
  FolderRow,
  FolderTreeItem,
  flattenTree,
} from "./SharedFilesTree";

const ROOT_KEY = "__root__";

/**
 * `api` prop lets this workspace be reused for both the tenant-wide
 * "Ortak Dosyalar" (sharedFilesApi — default) and per-user "Kişisel
 * Dosyalar" (personalFilesApi). Both adapters expose the same shape
 * (listFolders / createFolder / listFiles / upload / …) plus feature
 * flags (`supportsPassword`, `adminOnlyMutations`) so the UI can toggle
 * password-related affordances and RBAC gates.
 */
export default function SharedFilesView({ api = sharedFilesApi, initialFolderId = null }) {
  const { user } = useAuthStore();
  // Folder create/rename/delete + file password/move flags are gated
  // on elevated roles for the tenant-wide "Ortak Dosyalar" adapter
  // (adminOnlyMutations = true). Personal drive stays open — each user
  // owns their folders. Ultra admin ⊇ super admin.
  const canMutate = api.adminOnlyMutations
    ? user?.role === "super_admin" || user?.role === "ultra_admin"
    : true;
  // Dosya YÜKLEME herkese açık (klasör yönetimi/silme admin'de kalır).
  const canUpload = true;

  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(initialFolderId || ROOT_KEY);
  const [expanded, setExpanded] = useState(new Set([ROOT_KEY]));
  const [loading, setLoading] = useState(false);

  // Deep link (?folder=<id>) — klasörler yüklenince hedef klasörün tüm
  // atalarını genişlet ki ağaçta görünür olsun.
  useEffect(() => {
    if (!initialFolderId || folders.length === 0) return;
    setExpanded((prev) => {
      const n = new Set(prev);
      let node = folders.find((f) => f.id === initialFolderId);
      while (node) {
        n.add(node.id);
        node = folders.find((f) => f.id === node.parent_id);
      }
      return n;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFolderId, folders.length]);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  // Which folder the "New folder" dialog will create the folder under.
  // null = root. Set when user clicks the top "Yeni" button (uses
  // currentFolder) or a per-folder "+" affordance in the tree.
  const [newFolderParent, setNewFolderParent] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  // Bulk selection — Set<file_id>. Cleared when the current folder changes
  // so users don't accidentally act on stale selections.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const moveFiles = async (fileIds, targetFolderId) => {
    if (fileIds.length === 0) return;
    try {
      const res = await api.moveFiles(fileIds, targetFolderId);
      toast.success(
        res.moved === 1
          ? "Dosya taşındı"
          : `${res.moved} dosya taşındı`,
      );
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [fs, fls] = await Promise.all([
        api.listFolders(),
        api.listFiles(
          currentFolder === ROOT_KEY ? null : currentFolder,
        ),
      ]);
      setFolders(fs);
      setFiles(fls);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setSelectedIds(new Set());
  }, [currentFolder]);

  const rootFolders = useMemo(
    () => folders.filter((f) => !f.parent_id),
    [folders],
  );
  const childrenOf = (parentId) =>
    folders.filter((f) => f.parent_id === parentId);

  const flatFolders = useMemo(
    () => flattenTree(rootFolders, expanded, childrenOf),
    // childrenOf is derived from `folders`; recomputing on folders change is cheap.
    [rootFolders, expanded, folders], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await api.createFolder(name, newFolderParent);
      toast.success(`"${name}" klasörü oluşturuldu`);
      setNewFolderOpen(false);
      setNewFolderName("");
      // Auto-expand the parent so the newly created child is visible.
      if (newFolderParent) {
        setExpanded((prev) => new Set(prev).add(newFolderParent));
      }
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    }
  };

  const openNewFolderForCurrent = () => {
    setNewFolderParent(currentFolder === ROOT_KEY ? null : currentFolder);
    setNewFolderName("");
    setNewFolderOpen(true);
  };

  const openNewFolderUnder = (folder) => {
    setNewFolderParent(folder.id);
    setNewFolderName("");
    setNewFolderOpen(true);
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    const name = renameValue.trim();
    if (!name || name === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    try {
      await api.renameFolder(renameTarget.id, name);
      toast.success("Klasör yeniden adlandırıldı");
      setRenameTarget(null);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    }
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    try {
      const res = await api.removeFolder(deleteFolderTarget.id);
      toast.success(
        `Klasör silindi (${res.removed_files || 0} dosya, ${
          res.removed_folders - 1 || 0
        } alt klasör)`,
      );
      if (currentFolder === deleteFolderTarget.id) setCurrentFolder(ROOT_KEY);
      setDeleteFolderTarget(null);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    }
  };

  const removeFile = async (f) => {
    if (!window.confirm(`"${f.name}" dosyasını sil?`)) return;
    try {
      await api.remove(f.id);
      toast.success("Dosya silindi");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    }
  };

  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 200);
  };

  const downloadOne = async (f) => {
    // Şifreli dosyanın şifre akışı önizleme modalında — oraya yönlendir.
    if (f.has_password && !canMutate && api.supportsPassword) {
      setPreviewFile(f);
      return;
    }
    try {
      const res = await http.get(api.downloadUrl(f.id), {
        responseType: "blob",
      });
      saveBlob(res.data, f.name);
    } catch {
      toast.error("Dosya indirilemedi");
    }
  };

  const bulkDownload = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.bulkDownload(ids);
      saveBlob(res.data, "serpix-dosyalar.zip");
      const skipped = Number(res.headers?.["x-skipped-count"] || 0);
      if (skipped > 0)
        toast.warning(`${skipped} dosya atlandı (şifreli veya bulunamadı)`);
      else toast.success(`${ids.length} dosya ZIP olarak indirildi`);
    } catch {
      toast.error("Toplu indirme başarısız");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    try {
      const res = await api.bulkDelete(ids);
      toast.success(`${res.deleted ?? ids.length} dosya silindi`);
      setConfirmBulkDelete(false);
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const breadcrumb = useMemo(() => {
    if (currentFolder === ROOT_KEY) return [];
    const trail = [];
    let node = folders.find((f) => f.id === currentFolder);
    while (node) {
      trail.unshift(node);
      node = folders.find((f) => f.id === node.parent_id);
    }
    return trail;
  }, [folders, currentFolder]);

  return (
    <div
      className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]"
      data-testid="shared-files-view"
    >
      <aside className="rounded-xl border border-zinc-200 bg-white p-3">
        <div className="mb-2 flex items-center justify-between px-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            Klasörler
          </div>
          {canMutate && (
            <button
              type="button"
              onClick={openNewFolderForCurrent}
              data-testid="shared-files-new-folder-btn"
              className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[11px] font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
              title="Yeni klasör"
            >
              <FolderPlus className="h-3.5 w-3.5" />
              Yeni
            </button>
          )}
        </div>
        <ul className="space-y-0.5 text-sm">
          <FolderRow
            active={currentFolder === ROOT_KEY}
            onClick={() => setCurrentFolder(ROOT_KEY)}
            Icon={Home}
            label="Ana klasör"
            testid="shared-folder-root"
            onDropFiles={
              canMutate ? (fileIds) => moveFiles(fileIds, null) : null
            }
          />
          {flatFolders.map(({ folder, depth }) => (
            <FolderTreeItem
              key={folder.id}
              folder={folder}
              depth={depth}
              current={currentFolder}
              expanded={expanded}
              onToggle={toggleExpanded}
              onSelect={setCurrentFolder}
              hasChildren={childrenOf(folder.id).length > 0}
              onAddChild={canMutate ? openNewFolderUnder : null}
              onRename={
                canMutate
                  ? (f) => {
                      setRenameTarget(f);
                      setRenameValue(f.name);
                    }
                  : null
              }
              onDelete={
                canMutate ? (f) => setDeleteFolderTarget(f) : null
              }
              onDropFiles={
                canMutate
                  ? (fileIds) => moveFiles(fileIds, folder.id)
                  : null
              }
            />
          ))}
        </ul>
      </aside>

      <section className="rounded-xl border border-zinc-200 bg-white p-4">
        <SharedFilesHeader
          breadcrumb={breadcrumb}
          fileCount={files.length}
          canUpload={canUpload}
          onUpload={() => setUploadOpen(true)}
          onCrumbClick={setCurrentFolder}
        />

        <SharedFilesGrid
          loading={loading}
          files={files}
          canMutate={canMutate}
          onPreview={setPreviewFile}
          onDelete={removeFile}
          onDownload={downloadOne}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
        />

        {selectedIds.size > 0 && (
          <BulkActionBar
            count={selectedIds.size}
            canMutate={canMutate}
            busy={bulkBusy}
            folders={folders}
            onMove={(folderId) =>
              moveFiles(Array.from(selectedIds), folderId)
            }
            onDownload={bulkDownload}
            onDelete={() => setConfirmBulkDelete(true)}
            onCancel={() => setSelectedIds(new Set())}
          />
        )}
      </section>

      <NewFolderDialog
        open={newFolderOpen}
        onOpenChange={setNewFolderOpen}
        name={newFolderName}
        setName={setNewFolderName}
        onSave={createFolder}
        parentLabel={
          newFolderParent
            ? folders.find((f) => f.id === newFolderParent)?.name || null
            : null
        }
      />

      <RenameFolderDialog
        target={renameTarget}
        value={renameValue}
        setValue={setRenameValue}
        onCancel={() => setRenameTarget(null)}
        onSave={submitRename}
      />

      <DeleteFolderDialog
        target={deleteFolderTarget}
        onCancel={() => setDeleteFolderTarget(null)}
        onConfirm={confirmDeleteFolder}
      />

      <AlertDialog
        open={confirmBulkDelete}
        onOpenChange={(o) => !o && !bulkBusy && setConfirmBulkDelete(false)}
      >
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedIds.size} dosya silinsin mi?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Seçilen dosyalar kalıcı olarak silinecek. Bu işlem geri
              alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDelete}
              disabled={bulkBusy}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkBusy ? "Siliniyor..." : "Kalıcı olarak sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SharedFileUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        folderId={currentFolder === ROOT_KEY ? null : currentFolder}
        onUploaded={load}
        filesApi={api}
      />

      <SharedFilePreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
        filesApi={api}
      />
    </div>
  );
}

function SharedFilesHeader({
  breadcrumb,
  fileCount,
  canUpload,
  onUpload,
  onCrumbClick,
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1 text-sm text-zinc-500">
          <Home className="h-3.5 w-3.5" />
          {breadcrumb.length === 0 ? (
            <span>Ana klasör</span>
          ) : (
            breadcrumb.map((b, i) => (
              <span key={b.id} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 text-zinc-300" />
                <button
                  type="button"
                  onClick={() => onCrumbClick(b.id)}
                  className={
                    i === breadcrumb.length - 1
                      ? "font-semibold text-zinc-900"
                      : "hover:text-zinc-900"
                  }
                >
                  {b.name}
                </button>
              </span>
            ))
          )}
        </div>
        <div className="mt-1 text-[11px] text-zinc-400">
          {fileCount} dosya
        </div>
      </div>
      {canUpload && (
        <Button
          onClick={onUpload}
          data-testid="shared-files-upload-btn"
          className="gap-2 bg-zinc-900 text-white hover:bg-zinc-800"
        >
          <Upload className="h-4 w-4" />
          Dosya yükle
        </Button>
      )}
    </div>
  );
}

function SharedFilesGrid({
  loading,
  files,
  canMutate,
  onPreview,
  onDelete,
  onDownload,
  selectedIds,
  onToggleSelect,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (files.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-10 text-center text-sm text-zinc-500">
        Bu klasörde dosya yok.
      </div>
    );
  }
  return (
    <ul
      className="divide-y divide-zinc-100 overflow-hidden rounded-lg border border-zinc-200 bg-white"
      data-testid="shared-files-list"
    >
      {files.map((f) => (
        <SharedFileCard
          key={f.id}
          file={f}
          canMutate={canMutate}
          onPreview={() => onPreview(f)}
          onDelete={() => onDelete(f)}
          onDownload={() => onDownload(f)}
          selected={selectedIds?.has(f.id)}
          onToggleSelect={() => onToggleSelect?.(f.id)}
          selectedIds={selectedIds}
        />
      ))}
    </ul>
  );
}

/** Compact file row — the WHOLE row opens the preview modal; the
 *  checkbox powers bulk actions and stays clickable for everyone. */
function SharedFileCard({
  file,
  canMutate,
  onPreview,
  onDelete,
  onDownload,
  selected,
  onToggleSelect,
  selectedIds,
}) {
  const Icon = iconFor(file.mime_type);

  // What to hand off in the drag payload:
  //  - If the file is one of many selected → move ALL selected files.
  //  - Otherwise → move just this file.
  const dragIds =
    selected && selectedIds && selectedIds.size > 1
      ? Array.from(selectedIds)
      : [file.id];

  const handleDragStart = (e) => {
    if (!canMutate) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(
      "application/serpix-file-ids",
      JSON.stringify(dragIds),
    );
    // Also set a text fallback for compatibility.
    e.dataTransfer.setData("text/plain", dragIds.join(","));
  };

  return (
    <li
      className={`group flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors ${
        selected ? "bg-violet-50/70" : "hover:bg-violet-50/40"
      }`}
      data-testid={`shared-file-card-${file.id}`}
      draggable={canMutate}
      onDragStart={handleDragStart}
      onClick={onPreview}
      title="Önizlemek için tıklayın"
    >
      <span
        onClick={(e) => e.stopPropagation()}
        className="flex shrink-0 items-center"
      >
        <Checkbox
          checked={!!selected}
          onCheckedChange={onToggleSelect}
          data-testid={`shared-file-select-${file.id}`}
        />
      </span>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-zinc-100 to-zinc-200 text-zinc-600">
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="truncate text-[13px] font-medium text-zinc-900"
          title={file.name}
        >
          {file.name}
          {file.has_password && (
            <Lock
              className="ml-1 inline h-3.5 w-3.5 -translate-y-0.5 text-amber-600"
              data-testid={`shared-file-lock-${file.id}`}
              aria-label="Şifreli dosya"
            />
          )}
        </div>
        <div className="mt-0.5 truncate text-[11px] text-zinc-500">
          {formatSize(file.size)} · {file.mime_type || "—"}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          title="İndir"
          data-testid={`shared-file-download-${file.id}`}
          className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
        >
          <Download className="h-4 w-4" />
        </button>
        {canMutate && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Dosyayı sil"
            className="rounded-md p-1.5 text-rose-500 transition-colors hover:bg-rose-50 hover:text-rose-700"
            data-testid={`shared-file-delete-${file.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * Floating action bar shown when at least one file is selected.
 * Provides quick move (via a folder picker dropdown) + clear-selection.
 */
function BulkActionBar({
  count,
  canMutate,
  busy,
  folders,
  onMove,
  onDownload,
  onDelete,
  onCancel,
}) {
  const [moveOpen, setMoveOpen] = useState(false);

  return (
    <>
      <div
        className="sticky bottom-3 mt-4 flex flex-wrap items-center gap-2 rounded-full border border-zinc-200 bg-white p-1.5 pl-4 shadow-lg"
        data-testid="shared-files-bulk-bar"
      >
        <div className="mr-1 text-sm font-medium text-zinc-800">
          {count} dosya seçildi
        </div>
        <Button
          size="sm"
          onClick={onDownload}
          disabled={busy}
          data-testid="bulk-download-btn"
          className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          İndir (ZIP)
        </Button>
        {canMutate && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMoveOpen(true)}
            data-testid="bulk-move-btn"
            className="gap-1.5"
          >
            <Move className="h-3.5 w-3.5" />
            Taşı
          </Button>
        )}
        {canMutate && (
          <Button
            size="sm"
            onClick={onDelete}
            disabled={busy}
            data-testid="bulk-delete-btn"
            className="gap-1.5 bg-rose-600 text-white hover:bg-rose-700"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Sil
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          data-testid="bulk-cancel-btn"
          className="gap-1.5"
        >
          <X className="h-3.5 w-3.5" />
          İptal
        </Button>
      </div>

      <MoveFilesDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        folders={folders}
        onPick={(folderId) => {
          setMoveOpen(false);
          onMove(folderId);
        }}
      />
    </>
  );
}

function MoveFilesDialog({ open, onOpenChange, folders, onPick }) {
  // Sort folders by name for a stable pick list (Kök always on top).
  const sorted = useMemo(
    () => [...folders].sort((a, b) => a.name.localeCompare(b.name, "tr")),
    [folders],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="move-files-dialog"
      >
        <DialogHeader>
          <DialogTitle>Nereye taşınsın?</DialogTitle>
          <DialogDescription>
            Seçilen dosyaların yeni klasörünü seç.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto rounded-md border border-zinc-200">
          <button
            type="button"
            onClick={() => onPick(null)}
            data-testid="move-target-root"
            className="flex w-full items-center gap-2 border-b border-zinc-100 px-3 py-2 text-left text-sm hover:bg-zinc-50"
          >
            <Home className="h-3.5 w-3.5 text-zinc-500" />
            <span>Ana klasör</span>
          </button>
          {sorted.length === 0 ? (
            <div className="p-3 text-xs text-zinc-500">Klasör yok.</div>
          ) : (
            sorted.map((f) => (
              <button
                type="button"
                key={f.id}
                onClick={() => onPick(f.id)}
                data-testid={`move-target-${f.id}`}
                className="flex w-full items-center gap-2 border-b border-zinc-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-zinc-50"
              >
                <FolderPlus className="h-3.5 w-3.5 text-amber-500" />
                <span className="truncate">{f.name}</span>
              </button>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Vazgeç
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewFolderDialog({
  open,
  onOpenChange,
  name,
  setName,
  onSave,
  parentLabel,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-folder-dialog">
        <DialogHeader>
          <DialogTitle>Yeni klasör</DialogTitle>
          <DialogDescription>
            {parentLabel
              ? `"${parentLabel}" altında yeni bir alt klasör oluştur.`
              : "Ana klasör altında yeni bir klasör oluştur."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="new-folder-name">Klasör adı</Label>
          <Input
            id="new-folder-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSave()}
            placeholder="Örn: Sözleşmeler"
            data-testid="new-folder-name-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Vazgeç
          </Button>
          <Button
            onClick={onSave}
            disabled={!name.trim()}
            data-testid="new-folder-save-btn"
          >
            Oluştur
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameFolderDialog({ target, value, setValue, onCancel, onSave }) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="rename-folder-dialog">
        <DialogHeader>
          <DialogTitle>Klasörü yeniden adlandır</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Yeni ad</Label>
          <Input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSave()}
            data-testid="rename-folder-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Vazgeç
          </Button>
          <Button onClick={onSave} data-testid="rename-folder-save-btn">
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteFolderDialog({ target, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!target} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="delete-folder-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Klasörü sil?</AlertDialogTitle>
          <AlertDialogDescription>
            <b>{target?.name}</b> klasörü ve içindeki tüm alt klasörler +
            dosyalar kalıcı olarak silinecek. Bu işlem geri alınamaz.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Vazgeç</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-red-600 text-white hover:bg-red-700"
            data-testid="delete-folder-confirm-btn"
          >
            Kalıcı olarak sil
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
